import React, { useMemo } from 'react';
import { User, Users, MapPin, Trophy, Dumbbell, X, Search, UsersRound, Calendar, Star, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

// Icon mapping for entity types
const entityIcons = {
  user: User,
  team: Users,
  facility: MapPin,
  tournament: Trophy,
  sport_type: Dumbbell,
  group: UsersRound,
  event: Calendar
};

// Display names for entity types
const entityLabels = {
  user: 'Players',
  team: 'Teams',
  facility: 'Facilities',
  tournament: 'Tournaments',
  sport_type: 'Sports',
  group: 'Groups',
  event: 'Events'
};

const SearchResults = ({
  results,
  isLoading,
  query,
  onClose,
  onResultClick,
  searchModes,
  locationEnabled = false,
}) => {
  const { theme } = useTheme();

  // Check if there are any results
  const hasResults = results && Object.values(results).some(r => r.items?.length > 0);

  // Extract "Best Matches" (items with matchType 'both') and remaining items
  const { bestMatches, remainingResults } = useMemo(() => {
    if (!results) return { bestMatches: [], remainingResults: {} };

    const best = [];
    const remaining = {};

    Object.entries(results).forEach(([type, data]) => {
      if (!data.items || data.items.length === 0) return;

      const bothItems = data.items.filter(item => item.matchType === 'both');
      const otherItems = data.items.filter(item => item.matchType !== 'both');

      // Add 'both' items to best matches with their type info
      bothItems.forEach(item => {
        best.push({ ...item, entityType: type });
      });

      // Keep other items in remaining results
      if (otherItems.length > 0) {
        remaining[type] = {
          ...data,
          items: otherItems,
          total: otherItems.length
        };
      }
    });

    // Sort best matches by score
    best.sort((a, b) => (b.score || 0) - (a.score || 0));

    return { bestMatches: best, remainingResults: remaining };
  }, [results]);

  const handleResultClick = (item) => {
    if (onResultClick) {
      onResultClick(item);
    }
    onClose();
  };

  // Get color for match type badge
  const getMatchTypeColor = (matchType) => {
    if (matchType === 'semantic') {
      return 'bg-purple-100 text-purple-700';
    }
    if (matchType === 'both') {
      return 'bg-green-100 text-green-700';
    }
    return 'bg-blue-100 text-blue-700';
  };

  // Get label for match type
  const getMatchTypeLabel = (matchType) => {
    if (matchType === 'semantic') return 'AI';
    if (matchType === 'both') return 'exact+AI';
    return 'exact';
  };

  return (
    <div className={`absolute top-full left-0 right-0 mt-2 ${theme.card} rounded-lg ${theme.shadow} border ${theme.border} z-50 max-h-[70vh] overflow-hidden`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${theme.border} flex items-center justify-between`}>
        <div className="flex items-center gap-x-2">
          <Search className={`w-4 h-4 ${theme.textMuted}`} />
          <span className={`text-sm ${theme.textSecondary}`}>
            {query ? `Results for "${query}"` : 'Search'}
          </span>
          {searchModes && (
            <div className="flex items-center gap-x-1 ml-2">
              {searchModes.semantic && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-600">AI</span>
              )}
              {searchModes.keyword && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">Keyword</span>
              )}
              {locationEnabled && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-600 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Nearby
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded ${theme.cardHover}`}
        >
          <X className={`w-4 h-4 ${theme.textMuted}`} />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className={`w-6 h-6 animate-spin ${theme.textMuted}`} />
            <span className={`ml-2 ${theme.textMuted}`}>Searching...</span>
          </div>
        ) : !hasResults ? (
          <div className="py-8 text-center">
            <Search className={`w-12 h-12 mx-auto ${theme.textMuted} opacity-50`} />
            <p className={`mt-2 ${theme.textMuted}`}>
              {query ? `No results found for "${query}"` : 'Start typing to search'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* Best Matches Section - Items with matchType 'both' */}
            {bestMatches.length > 0 && (
              <div className="py-2">
                <div className={`px-4 py-2 flex items-center gap-x-2 ${theme.backgroundSecondary}`}>
                  <Star className={`w-4 h-4 text-yellow-500`} />
                  <span className={`text-xs font-semibold uppercase tracking-wide text-yellow-600`}>
                    Best Matches
                  </span>
                  <span className={`text-xs ${theme.textMuted}`}>
                    ({bestMatches.length})
                  </span>
                </div>

                {bestMatches.map((item, index) => {
                  const Icon = entityIcons[item.entityType] || Search;
                  return (
                    <div
                      key={`best-${item.entityType}-${item.id}-${index}`}
                      onClick={() => handleResultClick(item)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${theme.cardHover} border-l-4 border-yellow-400`}
                    >
                      <div className="flex items-start gap-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.entityType === 'user' ? 'bg-blue-100' :
                          item.entityType === 'team' ? 'bg-green-100' :
                          item.entityType === 'facility' ? 'bg-orange-100' :
                          item.entityType === 'tournament' ? 'bg-purple-100' :
                          item.entityType === 'group' ? 'bg-teal-100' :
                          item.entityType === 'event' ? 'bg-indigo-100' :
                          'bg-gray-100'
                        }`}>
                          {item.profileImage ? (
                            <img
                              src={resolveImageUrl(item.profileImage)}
                              alt={item.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <Icon className={`w-5 h-5 ${
                              item.entityType === 'user' ? 'text-blue-600' :
                              item.entityType === 'team' ? 'text-green-600' :
                              item.entityType === 'facility' ? 'text-orange-600' :
                              item.entityType === 'tournament' ? 'text-purple-600' :
                              item.entityType === 'group' ? 'text-teal-600' :
                              item.entityType === 'event' ? 'text-indigo-600' :
                              'text-gray-600'
                            }`} />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-x-2">
                            <p className={`text-sm font-medium ${theme.text} truncate`}>
                              {item.name}
                            </p>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getMatchTypeColor(item.matchType)}`}>
                              {getMatchTypeLabel(item.matchType)}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600`}>
                              {entityLabels[item.entityType] || item.entityType}
                            </span>
                          </div>
                          {item.sportType && (
                            <p className="text-xs text-purple-600 truncate mt-0.5">
                              {item.sportType}
                            </p>
                          )}
                          {item.description && (
                            <p className={`text-xs ${theme.textMuted} truncate mt-0.5`}>
                              {item.description}
                            </p>
                          )}
                          {item.entityType === 'group' && (
                            <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                              {item.memberCount ?? 0} {item.memberCount === 1 ? 'member' : 'members'} · {item.postCount ?? 0} posts
                            </p>
                          )}
                          {item.distanceText && (
                            <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {item.distanceText} away
                            </p>
                          )}
                          {item.score && (
                            <div className="mt-1">
                              <div className="flex items-center gap-x-1">
                                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-yellow-500"
                                    style={{ width: `${Math.round(item.score * 100)}%` }}
                                  />
                                </div>
                                <span className={`text-xs ${theme.textMuted}`}>
                                  {Math.round(item.score * 100)}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Remaining Results by Entity Type */}
            {Object.entries(remainingResults).map(([type, data]) => {
              if (!data.items || data.items.length === 0) return null;

              const Icon = entityIcons[type] || Search;
              const label = entityLabels[type] || type;

              return (
                <div key={type} className="py-2">
                  {/* Section Header */}
                  <div className={`px-4 py-2 flex items-center gap-x-2 ${theme.backgroundSecondary}`}>
                    <Icon className={`w-4 h-4 ${theme.textMuted}`} />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted}`}>
                      {label}
                    </span>
                    <span className={`text-xs ${theme.textMuted}`}>
                      ({data.total}{data.hasMore ? '+' : ''})
                    </span>
                  </div>

                  {/* Items */}
                  {data.items.map((item, index) => (
                    <div
                      key={`${type}-${item.id}-${index}`}
                      onClick={() => handleResultClick(item)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${theme.cardHover}`}
                    >
                      <div className="flex items-start gap-x-3">
                        {/* Avatar/Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          type === 'user' ? 'bg-blue-100' :
                          type === 'team' ? 'bg-green-100' :
                          type === 'facility' ? 'bg-orange-100' :
                          type === 'tournament' ? 'bg-purple-100' :
                          type === 'group' ? 'bg-teal-100' :
                          type === 'event' ? 'bg-indigo-100' :
                          'bg-gray-100'
                        }`}>
                          {item.profileImage ? (
                            <img
                              src={resolveImageUrl(item.profileImage)}
                              alt={item.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <Icon className={`w-5 h-5 ${
                              type === 'user' ? 'text-blue-600' :
                              type === 'team' ? 'text-green-600' :
                              type === 'facility' ? 'text-orange-600' :
                              type === 'tournament' ? 'text-purple-600' :
                              type === 'group' ? 'text-teal-600' :
                              type === 'event' ? 'text-indigo-600' :
                              'text-gray-600'
                            }`} />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-x-2">
                            <p className={`text-sm font-medium ${theme.text} truncate`}>
                              {item.name}
                            </p>
                            {item.matchType && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${getMatchTypeColor(item.matchType)}`}>
                                {getMatchTypeLabel(item.matchType)}
                              </span>
                            )}
                          </div>
                          {item.sportType && (
                            <p className="text-xs text-purple-600 truncate mt-0.5">
                              {item.sportType}
                            </p>
                          )}
                          {item.description && (
                            <p className={`text-xs ${theme.textMuted} truncate mt-0.5`}>
                              {item.description}
                            </p>
                          )}
                          {type === 'group' && (
                            <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                              {item.memberCount ?? 0} {item.memberCount === 1 ? 'member' : 'members'} · {item.postCount ?? 0} posts
                            </p>
                          )}
                          {item.distanceText && (
                            <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {item.distanceText} away
                            </p>
                          )}
                          {item.location && !item.distanceText && (
                            <p className={`text-xs ${theme.textMuted} mt-0.5 flex items-center gap-1`}>
                              <MapPin className="w-3 h-3" />
                              {item.location}
                            </p>
                          )}
                          {item.score && (
                            <div className="mt-1">
                              <div className="flex items-center gap-x-1">
                                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${item.matchType === 'semantic' ? 'bg-purple-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.round(item.score * 100)}%` }}
                                  />
                                </div>
                                <span className={`text-xs ${theme.textMuted}`}>
                                  {Math.round(item.score * 100)}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with hint */}
      {hasResults && (
        <div className={`px-4 py-2 border-t ${theme.border} ${theme.backgroundSecondary}`}>
          <p className={`text-xs ${theme.textMuted} text-center`}>
            Click on a result to view details
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
